import { useEffect, useRef } from 'react';
import {
  type AspectRatio,
  type Layer,
  type PresetTextLayer,
  type SuggestedFrame,
  type TextLayer,
  type TextPreset,
  aspectClass,
  pickAutoContrastColor,
  renderLayersToCanvas,
} from '../lib/composeRender';

// Resolve the text shown in the thumbnail. Prefers the AI-suggested copy for
// the active frame so the gallery previews the user's actual headline/tagline
// rather than generic placeholders. Falls back to placeholder strings before
// suggestCopy has resolved.
function resolveThumbnailText(layer: PresetTextLayer, frame?: SuggestedFrame): string {
  if (layer.role === 'static' || layer.role === 'cta') {
    return layer.staticText || 'Shop Now';
  }
  if (layer.role === 'headline') return frame?.headline || 'Headline';
  if (layer.role === 'tagline') return frame?.tagline || 'Your tagline here';
  if (layer.role === 'subtext') return frame?.subtext || 'Subtext';
  return '';
}

// Sample the average RGB color of an image by drawing it small onto an
// offscreen canvas and averaging every pixel. The downscaled sample is enough
// for a representative "tone color" — perceptually identical to averaging the
// full-resolution image but ~1000× cheaper.
function getImageAverageColor(img: HTMLImageElement): { r: number; g: number; b: number } {
  const SAMPLE = 32;
  const c = document.createElement('canvas');
  c.width = SAMPLE;
  c.height = SAMPLE;
  const ctx = c.getContext('2d');
  if (!ctx) return { r: 128, g: 128, b: 128 };
  ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
  const data = ctx.getImageData(0, 0, SAMPLE, SAMPLE).data;
  let r = 0, g = 0, b = 0;
  const pixels = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
  }
  return {
    r: Math.round(r / pixels),
    g: Math.round(g / pixels),
    b: Math.round(b / pixels),
  };
}

interface PresetThumbnailProps {
  preset: TextPreset;
  aspectRatio: AspectRatio;
  isSelected: boolean;
  onClick: () => void;
  // Optional generated image used as the canvas background so users see the
  // preset applied to their actual product photo. Falls back to a gradient.
  backgroundImage?: string;
  // Optional AI-suggested copy used to populate the role-based slots so the
  // thumbnail shows the user's actual headline/tagline.
  suggestedFrame?: SuggestedFrame;
}

export default function PresetThumbnail({
  preset,
  aspectRatio,
  isSelected,
  onClick,
  backgroundImage,
  suggestedFrame,
}: PresetThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Same fallback rule as applyPreset: use the aspect-specific layout if
  // authored, otherwise the 1:1 layout. Thumbnails should always render
  // *something* for any preset that has any layout.
  const layout = preset.layouts[aspectRatio] ?? preset.layouts['1:1'];

  useEffect(() => {
    if (!layout) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Internal pixel buffer (crisp on hi-dpi). Display size is controlled by
    // CSS (w-full h-auto) so the thumbnail flexes to its container — we no
    // longer pin a fixed 220px display size, which was clipping the canvas
    // when the parent button was narrower than 220px.
    const dpr = window.devicePixelRatio || 1;
    const logicalW = 220;
    // Use the actual aspect's reciprocal-ratio so 9:16, 16:9, etc. render
    // correctly in thumbnails (not just 1:1 / 4:5).
    const ratio = aspectRatio.split(':').map(Number);
    const ratioWH = (ratio[0] && ratio[1]) ? ratio[0] / ratio[1] : 1;
    const logicalH = Math.round(logicalW / ratioWH);
    canvas.width = logicalW * dpr;
    canvas.height = logicalH * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    let cancelled = false;

    const paintGradient = () => {
      const gradient = ctx.createLinearGradient(0, 0, 0, logicalH);
      gradient.addColorStop(0, '#9CA3AF');
      gradient.addColorStop(1, '#374151');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, logicalW, logicalH);
    };

    const drawOverlays = () => {
      if (cancelled) return;
      const layers: Layer[] = layout.layers.map((l) => {
        if (l.kind !== 'text') return l;
        const text = resolveThumbnailText(l, suggestedFrame);
        const textLayer: TextLayer = { ...l, kind: 'text' as const, text };
        // Mirror StepText's auto-contrast pass so thumbnails read correctly
        // on dark/light photos. Layers with a backgroundColor or an explicit
        // stroke already have built-in contrast / authored color control;
        // leave them alone.
        if (textLayer.backgroundColor) return textLayer;
        if (textLayer.textStrokeColor) return textLayer;
        const color = pickAutoContrastColor(ctx, logicalW, logicalH, textLayer);
        return { ...textLayer, color };
      });
      renderLayersToCanvas(ctx, logicalW, logicalH, layers);
    };

    if (backgroundImage) {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        // Use a solid fill in the average tone of the generated image instead
        // of repeating the photo across every thumbnail. Keeps auto-contrast
        // meaningful (text color flips against the same tone) while making
        // the gallery far less visually busy — the tile's job is to preview
        // the *layout*, not the photo.
        const { r, g, b } = getImageAverageColor(img);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fillRect(0, 0, logicalW, logicalH);
        drawOverlays();
      };
      img.onerror = () => {
        if (cancelled) return;
        paintGradient();
        drawOverlays();
      };
      img.src = backgroundImage;
    } else {
      paintGradient();
      drawOverlays();
    }

    return () => { cancelled = true; };
  }, [layout, aspectRatio, backgroundImage, suggestedFrame]);

  if (!layout) return null;

  // Pin the tile to the working canvas's aspect on the button itself — that
  // way the displayed size is exact regardless of how the browser interprets
  // the canvas element's intrinsic ratio. The canvas fills it absolutely.
  // Use the shared helper (lists every aspect as a static literal so
  // Tailwind's JIT generates the actual CSS). Don't reconstruct the class
  // string dynamically — JIT can't see it and the aspect rule won't fire.
  const aspectClassName = aspectClass(aspectRatio);

  return (
    <button
      onClick={onClick}
      title={preset.name}
      className={`group relative block w-full ${aspectClassName} rounded-lg overflow-hidden transition-all cursor-pointer ${
        isSelected ? 'ring-2 ring-primary ring-offset-1 ring-offset-white' : 'ring-1 ring-surface-dark/40 hover:ring-primary/40'
      }`}
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </button>
  );
}
